import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Stack,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircle from '@mui/icons-material/CheckCircle';
import EventNote from '@mui/icons-material/EventNote';
import WarningAmber from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { toast } from 'react-toastify';
import CompteRendusTable from './CompteRendusTable';
import CompteRenduModal from './CompteRenduModal';
import { useAuth } from '../../contexts/AuthContext'; // Importation du contexte d'authentification

const CompteRendus = () => {
  const { user } = useAuth(); // Récupération de l'utilisateur connecté
  const [rawApiData, setRawApiData] = useState([]);
  const [loading, setLoading] = useState(true);

  // États pour les modales d'ajout, d'édition, de suppression et de visualisation
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDeleteId, setReportToDeleteId] = useState(null);
  const [editingReportId, setEditingReportId] = useState(null);

  // États pour la modale de visualisation (icône œil)
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCompteRenduToView, setSelectedCompteRenduToView] = useState(null);

  // Contexte actif lors de la création/édition (Offre et Réservation ciblées)
  const [activeReservationContext, setActiveReservationContext] = useState(null);

  // États du formulaire de compte rendu
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportSuccess, setReportSuccess] = useState(null);

  const formTopRef = useRef(null);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPraticien, setFilterPraticien] = useState('all');
  const [filterComptesRendus, setFilterComptesRendus] = useState('all');

  const apiUrl = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('token');

  // Détermination dynamique du rolePath en fonction du rôle (Admin = admin, Praticien = praticien, Intervenant = intervenant)
  const rolePath = useMemo(() => {
    if (!user) return 'admin';
    if (Number(user.role_user || user.role) === 4) return 'praticien';
    if (Number(user.role_user || user.role) === 3) return 'intervenant';
    return 'admin';
  }, [user]);
console.log('role path', rolePath )
  const fetchCompteRendus = useCallback(async () => {
    try {
      setLoading(true);
      // Endpoint dynamique selon le rôle
      const endpoint = `${apiUrl.replace(/\/+$/, '')}/${rolePath}/a/all-reservations-comptes-rendus`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setRawApiData(rawData.filter(Boolean));
    } catch (err) {
      console.error('Erreur attrapée :', err);
      toast.error('Erreur de chargement des comptes rendus');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, rolePath]);

  useEffect(() => {
    fetchCompteRendus();
  }, [fetchCompteRendus]);

  useEffect(() => {
    if ((reportSuccess || reportError) && formTopRef.current) {
      formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [reportSuccess, reportError]);

  const praticiensList = useMemo(() => {
    if (!Array.isArray(rawApiData)) return [];
    if (rawApiData[0]?.id_user) {
      return rawApiData.map((p) => ({
        id: p.id_user,
        name: `${p.prenom_user} ${p.nom_user}`
      }));
    }
    return [];
  }, [rawApiData]);

  const offersList = useMemo(() => {
    if (!rawApiData || rawApiData.length === 0) return [];

    let offers = [];

    if (rawApiData[0]?.id_user) {
      rawApiData.forEach((praticien) => {
        praticien.offres_reservations?.forEach((item) => {
          offers.push({
            praticien_id: praticien.id_user,
            praticien_nom: `${praticien.prenom_user} ${praticien.nom_user}`,
            praticien_email: praticien.email_user,
            info_offre: item.info_offre,
            reservations: item.reservations || []
          });
        });
      });
    } else {
      rawApiData.forEach((item) => {
        offers.push({
          praticien_id: user?.id_user || 'me',
          praticien_nom: user ? `${user.prenom_user || ''} ${user.nom_user || ''}` : 'Moi',
          info_offre: item.info_offre,
          reservations: item.reservations || []
        });
      });
    }

    return offers;
  }, [rawApiData, user]);

  // FILTRAGE AVANCÉ DES OFFRES
  const filteredOffers = useMemo(() => {
    return offersList.filter((offer) => {
      const term = searchTerm.toLowerCase().trim();
      const praticienName = (offer.praticien_nom || '').toLowerCase();
      const offreTitle = (offer.info_offre?.titre || '').toLowerCase();

      const matchesClient = offer.reservations.some((res) => {
        const fullName = `${res.client?.prenom || ''} ${res.client?.nom || ''}`.toLowerCase();
        return fullName.includes(term);
      });

      const matchesSearch =
        !term || praticienName.includes(term) || offreTitle.includes(term) || matchesClient;

      const matchesPraticien =
        filterPraticien === 'all'
          ? true
          : String(offer.praticien_id) === String(filterPraticien);

      const hasMissing = offer.reservations.some(
        (res) => !res.comptes_rendus || res.comptes_rendus.length === 0
      );

      let matchesComptesRendusFilter = true;
      if (filterComptesRendus === 'missing') {
        matchesComptesRendusFilter = hasMissing;
      } else if (filterComptesRendus === 'completed') {
        matchesComptesRendusFilter = !hasMissing;
      }

      return matchesSearch && matchesPraticien && matchesComptesRendusFilter;
    });
  }, [offersList, searchTerm, filterPraticien, filterComptesRendus]);

  const handleOpenViewModal = (cr, offerContext, resContext) => {
    setSelectedCompteRenduToView({
      ...cr,
      praticien_nom: offerContext.praticien_nom,
      info_offre: offerContext.info_offre,
      client: resContext.client,
      reservation_start: resContext.start_time
    });
    setViewModalOpen(true);
  };

  const handleDownloadPDF = (report, offer, reservation) => {
    const printWindow = window.open('', '_blank');
    const infoOffre = offer?.info_offre || {};
    const offreTitre = infoOffre.titre || report.pratique || 'Prestation SOA';
    const offreLieu = infoOffre.address_ws ? `${infoOffre.address_ws}, ${infoOffre.city_ws}` : 'Locaux S.O.A';
    const praticienNom = offer?.praticien_nom || 'Praticien SOA';
    const clientNom = reservation?.client ? `${reservation.client.prenom} ${reservation.client.nom}` : 'Patient';

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="UTF-8">
        <title>Compte Rendu - ${report.numero_seance || 'Séance'}</title>
        <style>
          body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #1E293B; line-height: 1.6; padding: 40px; background-color: #FFFFFF; }
          .header { border-bottom: 3px solid #4f46e5; padding-bottom: 20px; margin-bottom: 25px; display: flex; justify-content: space-between; align-items: flex-start; }
          .logo-area { font-size: 20px; font-weight: 800; color: #4f46e5; text-transform: uppercase; letter-spacing: 1px; }
          .title { font-size: 22px; font-weight: bold; color: #0F172A; margin: 5px 0 0 0; }
          .subtitle { font-size: 14px; color: #64748B; margin-top: 5px; }
          .badge { background: #E0F2FE; color: #0284C7; padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: bold; text-transform: uppercase; display: inline-block; }
          .info-box { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; padding: 20px; margin-bottom: 25px; }
          .info-item { font-size: 13px; color: #475569; margin-bottom: 6px; }
          .info-item strong { color: #1E293B; }
          .section { background: #FFFFFF; border: 1px solid #E2E8F0; border-left: 4px solid #0EA5E9; border-radius: 8px; padding: 18px; margin-bottom: 18px; }
          .section.green { border-left-color: #10B981; }
          .section.indigo { border-left-color: #6366F1; }
          .section-title { font-size: 13px; font-weight: bold; text-transform: uppercase; color: #0EA5E9; margin-bottom: 8px; }
          .section-text { font-size: 14px; color: #334155; white-space: pre-line; }
          .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #94A3B8; border-top: 1px solid #E2E8F0; padding-top: 15px; }
          @media print { body { padding: 20px; } .section { break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo-area">Plateforme S.O.A</div>
            <div class="title">Compte Rendu & Suivi Médical</div>
            <div class="subtitle">Prestation : <strong>${offreTitre}</strong> | Patient : <strong>${clientNom}</strong></div>
          </div>
          <div>
            <span class="badge">${report.statut === 'valide' ? 'Validé & Signé' : 'Brouillon'}</span>
          </div>
        </div>

        <div class="info-box">
          <div>
            <div class="info-item"><strong>Séance :</strong> ${report.numero_seance || 'Séance n°1'}</div>
            <div class="info-item"><strong>Date de consultation :</strong> ${report.date_seance ? new Date(report.date_seance).toLocaleDateString('fr-FR') : '—'}</div>
            <div class="info-item"><strong>Lieu :</strong> ${offreLieu}</div>
          </div>
          <div>
            <div class="info-item"><strong>Intervenant / Praticien :</strong> ${praticienNom}</div>
            <div class="info-item"><strong>Date de rédaction :</strong> ${report.created_at ? new Date(report.created_at).toLocaleString('fr-FR') : '—'}</div>
          </div>
        </div>

        ${report.pratique ? `<div class="section"><div class="section-title">Pratique & Approche</div><div class="section-text">${report.pratique}</div></div>` : ''}
        ${report.perception_patient ? `<div class="section"><div class="section-title">Perception & État du Patient</div><div class="section-text">${report.perception_patient}</div></div>` : ''}
        ${report.resume_seance ? `<div class="section green"><div class="section-title">Résumé & Actes Réalisés</div><div class="section-text">${report.resume_seance}</div></div>` : ''}
        ${report.prevision_suite ? `<div class="section indigo"><div class="section-title">Plan de Soins & Suite</div><div class="section-text">${report.prevision_suite}</div></div>` : ''}

        <div class="footer">
          Plateforme S.O.A (Soins d'Onco-Accompagnement) • Document officiel généré le ${new Date().toLocaleDateString('fr-FR')}
        </div>

        <script>
          window.onload = function() { window.print(); window.onafterprint = function() { window.close(); } }
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleOpenCreateForReservation = (offer, reservation) => {
    setActiveReservationContext({ offer, reservation });
    setEditingReportId(null);
    setReportForm({
      numero_seance: 'Séance n°1',
      pratique: offer?.info_offre?.titre || '',
      date_seance: reservation?.start_time ? reservation.start_time.replace(' ', 'T').slice(0, 16) : new Date().toISOString().slice(0, 16),
      perception_patient: '',
      resume_seance: '',
      prevision_suite: '',
      statut: 'valide',
    });
    setReportError(null);
    setReportSuccess(null);
    setOpenReportDialog(true);
  };

  const handleOpenEdit = (report, offer, reservation) => {
    setActiveReservationContext({ offer, reservation });
    const reportId = report.id_comptes_rendus || report.id_compte_rendu || report.id;
    setEditingReportId(reportId);

    setReportForm({
      numero_seance: report.numero_seance || 'Séance n°1',
      pratique: report.pratique || offer?.info_offre?.titre || '',
      date_seance: report.date_seance ? report.date_seance.replace(' ', 'T').slice(0, 16) : new Date().toISOString().slice(0, 16),
      perception_patient: report.perception_patient || '',
      resume_seance: report.resume_seance || '',
      prevision_suite: report.prevision_suite || '',
      statut: report.statut || 'valide',
    });
    setReportError(null);
    setReportSuccess(null);
    setOpenReportDialog(true);
  };

  const [reportForm, setReportForm] = useState({
    numero_seance: 'Séance n°1',
    pratique: '',
    date_seance: new Date().toISOString().slice(0, 16),
    perception_patient: '',
    resume_seance: '',
    prevision_suite: '',
    statut: 'valide',
  });

  const handleReportChange = (e) => {
    const { name, value } = e.target;
    setReportForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveReport = async () => {
    if (!activeReservationContext) return;
    setReportSubmitting(true);
    setReportError(null);
    setReportSuccess(null);

    try {
      const isEditing = Boolean(editingReportId);
      const endpoint = isEditing
        ? `${apiUrl.replace(/\/+$/, '')}/${rolePath}/comptes-rendus/${editingReportId}`
        : `${apiUrl.replace(/\/+$/, '')}/${rolePath}/comptes-rendus`;

      const method = isEditing ? 'PUT' : 'POST';
      const reservationId = activeReservationContext.reservation?.id_offre_reservation;
      const patientProfileId = activeReservationContext.reservation?.client?.id_patient_profile || 1;

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          id_offre_reservation: reservationId,
          id_patient_profile: patientProfileId,
          ...reportForm,
        }),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error("Erreur serveur : L'API a renvoyé du HTML au lieu du JSON.");
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erreur lors de l’enregistrement du compte rendu.');
      }

      setReportSuccess(isEditing ? 'Compte rendu mis à jour avec succès !' : 'Compte rendu enregistré avec succès !');
      fetchCompteRendus();
      setTimeout(() => {
        setOpenReportDialog(false);
        setReportSuccess(null);
      }, 1500);
    } catch (err) {
      setReportError(err.message);
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleConfirmDelete = (reportId) => {
    if (!reportId) {
      toast.error("ID du compte rendu introuvable.");
      return;
    }
    setReportToDeleteId(reportId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteReport = async () => {
    if (!reportToDeleteId) return;

    try {
      const endpoint = `${apiUrl.replace(/\/+$/, '')}/${rolePath}/comptes-rendus/${reportToDeleteId}`;
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'Erreur lors de la suppression.');
      }

      setDeleteConfirmOpen(false);
      setReportToDeleteId(null);
      toast.success("Compte rendu supprimé avec succès.");
      fetchCompteRendus();
    } catch (err) {
      toast.error("Erreur : " + err.message);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      
      {/* En-tête */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography
            variant="h3"
            fontWeight="800"
            color="#1B2559"
            letterSpacing="-1px"
          >
            Comptes Rendus
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Consultez, ajoutez et gérez les comptes rendus par prestation et par réservation.
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
          <TextField
            placeholder="Rechercher offre, praticien, patient..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: '50px',
              width: { xs: '100%', sm: '320px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '50px',
                borderColor: '#e2e8f0',
              },
            }}
          />
        </Stack>
      </Stack>

      {/* Barre de Filtres Complète */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ color: '#64748b' }}>
          <FilterListIcon fontSize="small" />
          <Typography variant="body2" fontWeight={600}>Filtres :</Typography>
        </Stack>

        {/* 1. Filtre par Praticien (Affiché uniquement si admin ou liste multiple) */}
        {praticiensList.length > 0 && rolePath === 'admin' && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={filterPraticien}
              onChange={(e) => setFilterPraticien(e.target.value)}
              sx={{ borderRadius: '10px', backgroundColor: '#f8fafc' }}
            >
              <MenuItem value="all">Tous les praticiens</MenuItem>
              {praticiensList.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* 2. Filtre par État des Comptes Rendus */}
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <Select
            value={filterComptesRendus}
            onChange={(e) => setFilterComptesRendus(e.target.value)}
            sx={{ borderRadius: '10px', backgroundColor: '#f8fafc' }}
          >
            <MenuItem value="all">Tous les états de comptes rendus</MenuItem>
            <MenuItem value="missing" sx={{ color: '#dc2626', fontWeight: 600 }}>
              ⚠️ Soins individuels SANS compte rendu
            </MenuItem>
            <MenuItem value="completed" sx={{ color: '#16a34a', fontWeight: 600 }}>
              ✅ Soins individuels AVEC comptes rendus
            </MenuItem>
          </Select>
        </FormControl>

        <Chip
          label={`${filteredOffers.length} Offres trouvées`}
          size="small"
          sx={{ ml: 'auto', backgroundColor: '#e0e7ff', color: '#3730a3', fontWeight: 700 }}
        />
      </Paper>

      {/* Table Collapsible */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#4f46e5' }} />
        </Box>
      ) : (
        <CompteRendusTable
          data={filteredOffers}
          onViewCompteRendu={(cr, offerContext, resContext) => handleOpenViewModal(cr, offerContext, resContext)}
          onEditCompteRendu={(cr, offerContext, resContext) => handleOpenEdit(cr, offerContext, resContext)}
          onDeleteCompteRendu={(reportId) => handleConfirmDelete(reportId)}
          onAddCompteRendu={(offerContext, resContext) => handleOpenCreateForReservation(offerContext, resContext)}
          onDownloadPDF={(cr, offerContext, resContext) => handleDownloadPDF(cr, offerContext, resContext)}
        />
      )}

      {/* Modale de Visualisation */}
      <CompteRenduModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedCompteRenduToView(null);
        }}
        compteRendu={selectedCompteRenduToView}
      />

      {/* Modale de Confirmation de Suppression */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px', p: 1, maxWidth: '400px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#EF4444', fontWeight: 800 }}>
          <WarningAmber /> Confirmation de suppression
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#334155', fontSize: '14px' }}>
            Êtes-vous sûr de vouloir supprimer ce compte rendu ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit" sx={{ fontWeight: 600, textTransform: 'none' }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteReport}
            sx={{
              bgcolor: '#EF4444',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              '&:hover': { bgcolor: '#DC2626' },
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modale d'Édition / Création */}
      <Dialog 
        open={openReportDialog} 
        onClose={() => setOpenReportDialog(false)} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, bgcolor: '#3311CC', color: 'white', py: 2.5, px: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EventNote /> {editingReportId ? "Modifier le Compte Rendu" : "Rédaction du Compte Rendu"}
        </DialogTitle>
        
        <DialogContent sx={{ p: 4, pt: '32px !important', bgcolor: '#F8FAFC' }}>
          <div ref={formTopRef} />

          {reportError && <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>{reportError}</Alert>}
          {reportSuccess && <Alert severity="success" sx={{ mb: 3, borderRadius: '10px' }}>{reportSuccess}</Alert>}

          <Stack spacing={3}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Numéro / Intitulé de la séance"
                  name="numero_seance"
                  value={reportForm.numero_seance}
                  onChange={handleReportChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date et heure de la consultation"
                  name="date_seance"
                  type="datetime-local"
                  value={reportForm.date_seance}
                  onChange={handleReportChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
                />
              </Grid>
            </Grid>

            <TextField
              label="Spécialité / Pratique effectuée"
              name="pratique"
              value={reportForm.pratique}
              onChange={handleReportChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
            />

            <TextField
              label="Perception et ressentis du patient"
              name="perception_patient"
              multiline
              rows={3}
              value={reportForm.perception_patient}
              onChange={handleReportChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
            />

            <TextField
              label="Résumé détaillé de la séance & Actes réalisés"
              name="resume_seance"
              multiline
              rows={4}
              value={reportForm.resume_seance}
              onChange={handleReportChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
            />

            <TextField
              label="Prévisions thérapeutiques et suivi ultérieur"
              name="prevision_suite"
              multiline
              rows={3}
              value={reportForm.prevision_suite}
              onChange={handleReportChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
            />

            <TextField
              select
              label="Statut du rapport"
              name="statut"
              value={reportForm.statut}
              onChange={handleReportChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '12px', bgcolor: 'white' } }}
            >
              <MenuItem value="brouillon">Brouillon (En cours de rédaction)</MenuItem>
              <MenuItem value="valide">Validé et Signé</MenuItem>
            </TextField>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
              <Button onClick={() => setOpenReportDialog(false)} color="inherit" sx={{ fontWeight: 600 }}>
                Annuler
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveReport}
                disabled={reportSubmitting}
                startIcon={<CheckCircle />}
                sx={{
                  bgcolor: '#3311CC',
                  borderRadius: '12px',
                  fontWeight: 700,
                  px: 4,
                  py: 1.2,
                  boxShadow: '0 4px 14px rgb(51 17 204 / .3)',
                  '&:hover': { bgcolor: '#220899' },
                }}
              >
                {reportSubmitting ? 'Enregistrement...' : (editingReportId ? 'Mettre à jour' : 'Enregistrer le compte rendu')}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CompteRendus;